import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import {
  ArrowRight, Globe, Phone, Mail, MapPin, Briefcase, Wrench, TrendingUp,
  Headphones, FileSpreadsheet, Lock, Activity, ShieldCheck, CalendarCheck, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYWxsJTIwY2VudGVyJTIwb3BlcmF0b3J8ZW58MHx8fHwxNzc2MzE2NzEwfDA&ixlib=rb-4.1.0&q=85&w=1920";

const LANG_BADGES = [
  { code: "EN", label: "English", color: "#002FA7" },
  { code: "ES", label: "Español", color: "#FFB800" },
  { code: "FR", label: "Français", color: "#00C853" },
  { code: "HT", label: "Kreyòl Ayisyen", color: "#FF2A2A" },
];

const SERVICE_KEYS = [
  { key: "tech", icon: Wrench, accent: "#0A0A0A", testid: "service-technical-support" },
  { key: "customer", icon: Headphones, accent: "#002FA7", testid: "service-customer-support" },
  { key: "sales", icon: TrendingUp, accent: "#00C853", testid: "service-sales" },
  { key: "appt", icon: CalendarCheck, accent: "#FFB800", testid: "service-appointment-setting" },
  { key: "back", icon: FileSpreadsheet, accent: "#FF2A2A", testid: "service-back-office" },
  { key: "tailored", icon: Briefcase, accent: "#9333EA", testid: "service-tailored" },
];

const COMPLIANCE_PILLARS = [
  { icon: Lock, key: "security", testid: "compliance-security" },
  { icon: Activity, key: "monitor", testid: "compliance-monitoring" },
  { icon: ShieldCheck, key: "us", testid: "compliance-us-alignment" },
];

function handleSignIn() { window.location.href = "/login"; }
function handleStrategicCall() {
  window.location.href = "mailto:email@talk2support.com?subject=Strategic%20Partnership%20Call%20Request";
}

function NearshoreMap({ t }) {
  return (
    <svg viewBox="0 0 600 500" className="w-full h-auto" data-testid="nearshore-map" aria-label="US to Dominican Republic operations map">
      <defs>
        <linearGradient id="oceanGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A0A0A" />
          <stop offset="100%" stopColor="#0A0A0A" />
        </linearGradient>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#002FA7" />
          <stop offset="100%" stopColor="#FFB800" />
        </linearGradient>
      </defs>
      <rect width="600" height="500" fill="url(#oceanGrad)" />
      {[80, 160, 240, 320, 400].map((y) => (
        <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="#FFFFFF" strokeOpacity="0.04" strokeWidth="1" />
      ))}
      {[100, 200, 300, 400, 500].map((x) => (
        <line key={x} x1={x} y1="0" x2={x} y2="500" stroke="#FFFFFF" strokeOpacity="0.04" strokeWidth="1" />
      ))}
      <path
        d="M 90 110 L 250 100 L 340 130 L 370 180 L 360 230 L 300 260 L 230 250 L 160 220 L 110 180 Z"
        fill="#FFFFFF" fillOpacity="0.08" stroke="#002FA7" strokeWidth="2"
      />
      <path d="M 310 260 L 320 290 L 305 310 L 295 300 Z" fill="#FFFFFF" fillOpacity="0.08" stroke="#002FA7" strokeWidth="2" />
      <path
        d="M 380 360 L 440 358 L 455 372 L 445 388 L 400 390 L 375 380 Z"
        fill="#FFFFFF" fillOpacity="0.10" stroke="#FFB800" strokeWidth="2"
      />
      <path d="M 310 230 Q 360 300 415 372" fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeDasharray="4 4" />
      <circle cx="310" cy="230" r="8" fill="#002FA7" />
      <circle cx="310" cy="230" r="14" fill="none" stroke="#002FA7" strokeOpacity="0.35" strokeWidth="2" />
      <text x="270" y="200" fill="#FFFFFF" fontSize="11" fontWeight="900" letterSpacing="2" textAnchor="middle">U.S. HQ</text>
      <text x="270" y="215" fill="#FFFFFF" opacity="0.5" fontSize="9" letterSpacing="1.5" textAnchor="middle">
        {(t("landing.nearshore_pill_us_val") || "").toUpperCase()}
      </text>
      <circle cx="415" cy="372" r="8" fill="#FFB800" />
      <circle cx="415" cy="372" r="14" fill="none" stroke="#FFB800" strokeOpacity="0.4" strokeWidth="2" />
      <text x="460" y="368" fill="#FFFFFF" fontSize="11" fontWeight="900" letterSpacing="2">DOMINICAN REPUBLIC</text>
      <text x="460" y="382" fill="#FFFFFF" opacity="0.5" fontSize="9" letterSpacing="1.5">NEARSHORE HUB</text>
      <text x="20" y="480" fill="#FFFFFF" opacity="0.3" fontSize="9" letterSpacing="2" fontWeight="700">
        TALK2SUPPORT · GEOGRAPHIC FOOTPRINT
      </text>
    </svg>
  );
}

export default function LandingPage() {
  const { t } = useLanguage();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-14">
          <h1 className="text-base font-black text-white tracking-tight" data-testid="landing-logo">Talk2Support</h1>
          <div className="flex items-center gap-4">
            <LanguageToggle variant="landing" />
            {user ? (
              <Button
                data-testid="landing-go-dashboard"
                onClick={() => {
                  const routes = { "super-admin": "/admin", admin: "/admin", operator: "/operator", client: "/client" };
                  window.location.href = routes[user.role] || "/operator";
                }}
                className="h-8 px-4 text-xs font-bold tracking-[0.1em] uppercase bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none"
              >
                {t("nav.dashboard")}
              </Button>
            ) : (
              <Button
                data-testid="landing-sign-in"
                onClick={handleSignIn}
                className="h-8 px-4 text-xs font-bold tracking-[0.1em] uppercase bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none"
              >
                {t("common.sign_in")}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Enterprise BPO operations" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/85 via-[#0A0A0A]/75 to-[#002FA7]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16">
          <div className="max-w-4xl stagger-children">
            <div className="flex items-center gap-3 mb-5 animate-fade-in-up flex-wrap" style={{ opacity: 0 }}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#FFB800] font-bold">Talk2Support LLC</p>
              <span className="h-px w-8 bg-white/30" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 font-bold">
                {t("landing.hero_eyebrow_us")}
              </p>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.04] mb-6 animate-fade-in-up"
              style={{ opacity: 0 }}
              data-testid="hero-title"
            >
              {t("landing.hero_v2_part1")}{" "}
              <span className="text-[#FFB800]">{t("landing.hero_v2_part2")}</span>{" "}
              {t("landing.hero_v2_and")}{" "}
              <span className="text-[#002FA7] bg-white/95 px-2">{t("landing.hero_v2_part3")}</span>
            </h2>
            <p
              className="text-base sm:text-lg text-white/80 leading-relaxed mb-8 max-w-2xl animate-fade-in-up"
              style={{ opacity: 0 }}
              data-testid="hero-subtitle"
            >
              {t("landing.hero_v2_subtitle")}{" "}
              <span className="text-white font-bold">{t("landing.hero_v2_savings")}</span>{" "}
              {t("landing.hero_v2_outro")}
            </p>

            <div className="flex flex-wrap gap-2 mb-8 animate-fade-in-up" style={{ opacity: 0 }} data-testid="hero-lang-badges">
              {LANG_BADGES.map((lang) => (
                <span
                  key={lang.code}
                  className="flex items-center gap-1.5 px-3 py-1.5 border bg-white/5 backdrop-blur-sm"
                  style={{ borderColor: `${lang.color}66` }}
                  data-testid={`hero-lang-${lang.code.toLowerCase()}`}
                >
                  <span className="text-[10px] font-black tracking-[0.1em] px-1.5 py-0.5" style={{ background: lang.color, color: "#ffffff" }}>
                    {lang.code}
                  </span>
                  <span className="text-xs text-white/85 font-bold tracking-wide">{lang.label}</span>
                </span>
              ))}
              <span className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 bg-white/5 backdrop-blur-sm text-[10px] tracking-[0.15em] uppercase text-white/60 font-bold">
                {t("landing.badge_native_sync")}
              </span>
            </div>

            <div className="flex flex-wrap gap-3 animate-fade-in-up" style={{ opacity: 0 }}>
              <Button
                data-testid="hero-strategic-call"
                onClick={handleStrategicCall}
                className="h-12 px-7 text-sm font-bold tracking-[0.1em] uppercase bg-[#FFB800] hover:bg-[#E5A500] text-[#0A0A0A] rounded-none"
              >
                {t("landing.cta_strategic_call")} <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                data-testid="hero-sign-in"
                onClick={handleSignIn}
                variant="outline"
                className="h-12 px-7 text-sm font-bold tracking-[0.1em] uppercase border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] rounded-none bg-transparent"
              >
                {t("landing.cta_operator_signin")}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-16 bg-white/10 animate-fade-in-up" style={{ opacity: 0, animationDelay: "400ms" }}>
            {[
              { valKey: "landing.stat_cost", labelKey: "landing.stat_cost_label", testid: "cost-reduction" },
              { valKey: "landing.stat_langs", labelKey: "landing.stat_langs_label", testid: "native-coverage" },
              { val: "99.8%", labelKey: "landing.stat_uptime_label", testid: "voice-uptime" },
              { valKey: "landing.stat_sync", labelKey: "landing.stat_sync_label", testid: "nearshore-sync" },
            ].map((s) => (
              <div key={s.testid} className="bg-[#0A0A0A]/85 backdrop-blur-sm p-5 text-center" data-testid={`hero-stat-${s.testid}`}>
                <p className="text-2xl sm:text-3xl font-black text-white">{s.valKey ? t(s.valKey) : s.val}</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-bold mt-1">{t(s.labelKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearshore */}
      <section className="py-24 px-6 bg-[#F8F9FA]" data-testid="nearshore-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="border-4 border-[#0A0A0A] shadow-[8px_8px_0_0_rgba(0,47,167,1)]">
            <NearshoreMap t={t} />
          </div>
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#002FA7] font-bold mb-3">
              {t("landing.nearshore_eyebrow")}
            </p>
            <h3 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight mb-6 leading-tight">
              {t("landing.nearshore_title")}
            </h3>
            <p className="text-base text-[#4B5563] leading-relaxed mb-5">{t("landing.nearshore_copy_1")}</p>
            <p className="text-base text-[#4B5563] leading-relaxed mb-8">{t("landing.nearshore_copy_2")}</p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Building2, lk: "nearshore_pill_us", vk: "nearshore_pill_us_val", tid: "us-entity" },
                { icon: MapPin, lk: "nearshore_pill_hub", vk: "nearshore_pill_hub_val", tid: "ops-hub" },
                { icon: TrendingUp, lk: "nearshore_pill_savings", vk: "nearshore_pill_savings_val", tid: "savings" },
                { icon: Globe, lk: "nearshore_pill_talent", vk: "nearshore_pill_talent_val", tid: "talent" },
              ].map((item) => (
                <div key={item.tid} className="flex items-center gap-3 p-3 bg-white border border-[#E5E7EB]" data-testid={`nearshore-pill-${item.tid}`}>
                  <item.icon size={18} className="text-[#002FA7] shrink-0" />
                  <div>
                    <p className="text-[9px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold">{t(`landing.${item.lk}`)}</p>
                    <p className="text-sm text-[#0A0A0A] font-bold">{t(`landing.${item.vk}`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Matrix */}
      <section className="py-24 px-6 bg-white" data-testid="service-matrix-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div className="max-w-2xl">
              <p className="text-xs tracking-[0.3em] uppercase text-[#002FA7] font-bold mb-3">
                {t("landing.services_eyebrow")}
              </p>
              <h3 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight mb-4 leading-tight">
                {t("landing.services_title")}
              </h3>
              <p className="text-base text-[#4B5563] leading-relaxed">{t("landing.services_intro")}</p>
            </div>
            <div className="flex gap-2">
              {LANG_BADGES.map((l) => (
                <span key={l.code} className="text-[10px] font-black tracking-[0.1em] px-2 py-1" style={{ background: l.color, color: "#ffffff" }}>
                  {l.code}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {SERVICE_KEYS.map((svc) => (
              <div key={svc.testid} className="group bg-white p-7 transition-all duration-200 hover:bg-[#F8F9FA] flex flex-col" data-testid={svc.testid}>
                <div
                  className="w-12 h-12 flex items-center justify-center mb-5 transition-transform duration-200 group-hover:scale-110"
                  style={{ background: svc.accent }}
                >
                  <svc.icon size={22} className="text-white" />
                </div>
                <h4 className="text-lg font-black text-[#0A0A0A] mb-2 tracking-tight">{t(`landing.svc_${svc.key}`)}</h4>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-4 flex-1">{t(`landing.svc_${svc.key}_cap`)}</p>
                <div className="pt-4 border-t border-[#E5E7EB]">
                  <p className="text-[9px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold mb-1">{t("landing.service_target_outcome")}</p>
                  <p className="text-xs text-[#0A0A0A] font-bold leading-snug">{t(`landing.svc_${svc.key}_out`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#FFB800] font-bold mb-3">
              {t("landing.compliance_eyebrow")}
            </p>
            <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4 leading-tight">
              {t("landing.compliance_title")}
            </h3>
            <p className="text-base text-white/70 leading-relaxed">{t("landing.compliance_intro")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {COMPLIANCE_PILLARS.map((p) => (
              <div key={p.testid} className="bg-[#0A0A0A] p-7" data-testid={p.testid}>
                <div className="w-12 h-12 flex items-center justify-center mb-5 bg-white">
                  <p.icon size={22} className="text-[#002FA7]" />
                </div>
                <h4 className="text-lg font-black text-white mb-2 tracking-tight">{t(`landing.compliance_${p.key}`)}</h4>
                <p className="text-sm text-white/70 leading-relaxed">{t(`landing.compliance_${p.key}_desc`)}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-white/10 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {["trust_aes", "trust_tokens", "trust_qa", "trust_sla"].map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#FFB800]" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 font-bold">{t(`landing.${k}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#002FA7]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-white/60 font-bold mb-3">{t("landing.cta_eyebrow")}</p>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6 leading-tight">
            {t("landing.cta_title")}
          </h3>
          <p className="text-base text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t("landing.cta_subtitle")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              data-testid="cta-strategic-call"
              onClick={handleStrategicCall}
              className="h-12 px-7 text-sm font-bold tracking-[0.1em] uppercase bg-[#FFB800] hover:bg-[#E5A500] text-[#0A0A0A] rounded-none"
            >
              {t("landing.cta_strategic_call")} <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button
              data-testid="cta-sign-in"
              onClick={handleSignIn}
              variant="outline"
              className="h-12 px-7 text-sm font-bold tracking-[0.1em] uppercase border-white/40 text-white hover:bg-white hover:text-[#002FA7] rounded-none bg-transparent"
            >
              {t("landing.cta_operator_signin")}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <h4 className="text-base font-black text-white tracking-tight mb-2">Talk2Support LLC</h4>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#FFB800] font-bold mb-4">
              {t("landing.footer_brand_tagline")}
            </p>
            <p className="text-sm text-white/60 leading-relaxed">{t("landing.footer_brand_blurb")}</p>
          </div>

          <div data-testid="footer-admin">
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold mb-3">
              {t("landing.footer_admin")}
            </p>
            <p className="text-base text-white font-black mb-1">Jean P. Francois</p>
            <p className="text-xs text-white/60 tracking-[0.15em] uppercase font-bold">{t("landing.footer_exec")}</p>
          </div>

          <div data-testid="footer-contact">
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold mb-3">
              {t("landing.footer_contact")}
            </p>
            <div className="space-y-2.5">
              <a href="tel:+15617672477" className="flex items-center gap-2.5 text-sm text-white/85 hover:text-[#FFB800] transition-colors" data-testid="footer-phone-1">
                <Phone size={14} className="text-[#FFB800] shrink-0" />
                <span className="font-mono">+1 (561) 767-2477</span>
              </a>
              <a href="tel:+15618603496" className="flex items-center gap-2.5 text-sm text-white/85 hover:text-[#FFB800] transition-colors" data-testid="footer-phone-2">
                <Phone size={14} className="text-[#FFB800] shrink-0" />
                <span className="font-mono">+1 (561) 860-3496</span>
              </a>
              <a href="mailto:email@talk2support.com" className="flex items-center gap-2.5 text-sm text-white/85 hover:text-[#FFB800] transition-colors" data-testid="footer-email">
                <Mail size={14} className="text-[#FFB800] shrink-0" />
                <span>email@talk2support.com</span>
              </a>
              <div className="flex items-center gap-2.5 text-sm text-white/60 pt-1">
                <MapPin size={14} className="text-[#FFB800] shrink-0" />
                <span>{t("landing.footer_location")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-bold">
              &copy; 2026 Talk2Support LLC · {t("landing.footer_rights")}
            </p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/30 font-bold">
              {t("landing.footer_built")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
